import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ProcesosService from '../../services/ProcesosService';
import './ProcesosMenu.css';

const ProcesosMenu = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [procesos, setProcesos] = useState([]);
  const [procesosTotal, setProcesosTotal] = useState(0);
  const [procesosUnread, setProcesosUnread] = useState(0);
  const [procesosPage, setProcesosPage] = useState(1);
  const [showProcesos, setShowProcesos] = useState(false);
  const [selectedProceso, setSelectedProceso] = useState(null);
  const intervalRef = useRef(null);

  const formatProcesoDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('es-AR');
  };

  const getProcesoStatus = (proceso) => {
    const estado = String(proceso?.estado || '').trim().toLowerCase();
    if (proceso?.error || estado.includes('error')) {
      return { label: 'Error', className: 'error', icon: 'fas fa-exclamation-triangle' };
    }
    if (estado.includes('final') || proceso?.fechaFinalizacion) {
      return { label: 'Finalizado', className: 'finalizado', icon: 'fas fa-check-circle' };
    }
    if (estado.includes('encol')) {
      return { label: 'Encolado', className: 'encolado', icon: 'fas fa-clock' };
    }
    if (estado.includes('proces')) {
      return { label: 'Procesando', className: 'procesando', icon: 'fas fa-spinner fa-spin' };
    }
    return { label: 'En proceso', className: 'en-proceso', icon: 'fas fa-hourglass-half' };
  };

  const getProgressValue = (proceso) => {
    const value = Number(proceso?.porcentajeAvance ?? 0);
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(100, value));
  };

  const fetchProcesos = async (page = 1) => {
    if (authLoading || !user) return;
    try {
      const response = await ProcesosService.getProcesos({ page, pageSize: 10 });
      if (response?.ok) {
        setProcesos(response.procesos || []);
        setProcesosTotal(response.total || 0);
        setProcesosUnread(response.unreadCount || 0);
        setProcesosPage(response.page || page);
      }
    } catch (error) {
      console.error('Error al cargar procesos:', error);
    }
  };

  useEffect(() => {
    if (authLoading || !user) return;
    fetchProcesos(1);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => fetchProcesos(1), 15000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [authLoading, user]);

  const handleOpenProceso = async (proceso) => {
    setSelectedProceso(proceso);
    if (!proceso?.leido) {
      try {
        await ProcesosService.marcarLeido(proceso.id);
        setProcesos((prev) => prev.map((item) => (item.id === proceso.id ? { ...item, leido: true } : item)));
        setSelectedProceso((prev) => (prev?.id === proceso.id ? { ...prev, leido: true } : prev));
        setProcesosUnread((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error al marcar proceso como leído:', error);
      }
    }
  };

  return (
    <>
      <div className="procesos-menu">
        <button
          type="button"
          className="procesos-btn"
          onClick={() => setShowProcesos((prev) => !prev)}
          title="Solicitudes Iniciadas"
        >
          <i className="fas fa-tasks"></i>
          <span>Solicitudes Iniciadas</span>
          {procesosUnread > 0 && (
            <span className="procesos-badge">{procesosUnread}</span>
          )}
        </button>
        {showProcesos && (
          <div className="procesos-dropdown">
            <div className="procesos-dropdown-header">
              <span>Solicitudes recientes</span>
              <button
                type="button"
                className="procesos-see-all"
                onClick={() => {
                  setShowProcesos(false);
                  navigate('/procesos');
                }}
              >
                Ver todos
              </button>
              <span className="procesos-total" title={`Página ${procesosPage}`}>{procesosTotal}</span>
            </div>
            <div className="procesos-list">
              {procesos.length === 0 && (
                <div className="procesos-empty">No hay solicitudes.</div>
              )}
              {procesos.map((proceso) => {
                const status = getProcesoStatus(proceso);
                const progress = getProgressValue(proceso);
                return (
                  <button
                    key={proceso.id}
                    type="button"
                    className={`procesos-item ${proceso.leido ? '' : 'unread'}`}
                    onClick={() => handleOpenProceso(proceso)}
                  >
                    <div className="procesos-item-main">
                      <span className="procesos-item-title">{proceso.descripcion}</span>
                      <span className={`procesos-status ${status.className}`}>
                        <i className={status.icon}></i>
                        {status.label}
                      </span>
                    </div>
                    {(proceso.detalle || proceso.error) && (
                      <div className="procesos-item-detail">{proceso.detalle || proceso.error}</div>
                    )}
                    <div className="procesos-menu-progress">
                      <div className="procesos-menu-progress-track">
                        <div className={`procesos-menu-progress-fill ${status.className}`} style={{ width: `${progress}%` }}></div>
                      </div>
                      <span>{progress}%</span>
                    </div>
                    <div className="procesos-item-sub">
                      <span>{formatProcesoDate(proceso.fechaCreacion)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {selectedProceso && (
        <div className="modal-overlay" onClick={() => setSelectedProceso(null)}>
          <div className="modal-content procesos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                <i className="fas fa-tasks"></i>
                Detalle de la solicitud
              </h3>
              <button className="modal-close-btn" onClick={() => setSelectedProceso(null)} type="button">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="procesos-modal-summary">
                <div>
                  <span className="label">Descripción</span>
                  <span className="value">{selectedProceso.descripcion}</span>
                </div>
                <div>
                  <span className="label">Creado</span>
                  <span className="value">{formatProcesoDate(selectedProceso.fechaCreacion)}</span>
                </div>
                <div>
                  <span className="label">Finalizado</span>
                  <span className="value">{formatProcesoDate(selectedProceso.fechaFinalizacion)}</span>
                </div>
                <div>
                  <span className="label">Actualizado</span>
                  <span className="value">{formatProcesoDate(selectedProceso.fechaActualizacion)}</span>
                </div>
                <div>
                  <span className="label">Estado</span>
                  {(() => {
                    const status = getProcesoStatus(selectedProceso);
                    return (
                      <span className={`procesos-status ${status.className}`}>
                        <i className={status.icon}></i>
                        {status.label}
                      </span>
                    );
                  })()}
                </div>
                <div>
                  <span className="label">Avance</span>
                  <span className="value">{getProgressValue(selectedProceso)}%</span>
                </div>
              </div>
              {selectedProceso.detalle && (
                <div className="procesos-modal-detail">
                  <strong>Detalle:</strong> {selectedProceso.detalle}
                </div>
              )}
              {selectedProceso.error && (
                <div className="procesos-modal-error">
                  <strong>Error:</strong> {selectedProceso.error}
                </div>
              )}
              <div className="procesos-modal-body">
                <span className="label">Body</span>
                <pre>{selectedProceso.bodyMensaje}</pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProcesosMenu;
